import { Link } from "wouter";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Shield } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { FOOTER_NAV_LINKS } from "@/lib/siteNavigation";
import AccessibilityControls from "@/components/AccessibilityControls";

const socialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/immozwerg", icon: Instagram },
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61572975815321", icon: Facebook },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/andrea-heinz-822596145/", icon: Linkedin },
] as const;

/** Einheitlicher Footer mit derselben Seitenstruktur wie die Header-Navigation. */
export default function SiteFooter() {
  const { t } = useLocale();

  return (
    <footer className="site-footer text-white">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-9 sm:grid-cols-2 lg:grid-cols-[1.15fr_1fr_1.1fr]">
          <section>
            <div className="mb-4">
              <p className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Immozwerg</p>
              <p className="text-xs tracking-[0.2em] text-[#e8c96a]">Andrea Heinz · HAJ Immo e.K.</p>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-white/70">Immobilienmaklerin mit Herz, Ideen und Verstand für Güglingen, Heilbronn, Brackenheim und das Zabergäu.</p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-left" aria-label="HAJ Immo e.K., Güglingen, mit Maklererlaubnis nach Paragraph 34c Gewerbeordnung">
              <Shield size={17} className="shrink-0 text-[#e8c96a]" aria-hidden="true" />
              <div>
                <p className="text-xs font-bold text-white">HAJ Immo e.K. · Güglingen</p>
                <p className="text-[11px] text-white/65">Maklererlaubnis nach § 34c GewO</p>
              </div>
            </div>
          </section>

          <nav aria-label="Seiten im Footer">
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-white/80">Seiten</h2>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {FOOTER_NAV_LINKS.map((item) => (
                <Link key={item.href} href={item.href} className="immozwerg-footer-link text-sm">
                  {t(item.key)}
                </Link>
              ))}
            </div>
          </nav>

          <section>
            <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-white/80">Kontakt</h2>
            <div className="space-y-2 text-sm text-white/60">
              <a href="tel:+491738660756" className="immozwerg-footer-contact-card"><Phone size={15} aria-hidden="true" /><span><span className="immozwerg-footer-contact-label">Telefon</span>+49 173 8660756</span></a>
              <a href="mailto:hallo@haj-immokg.com" className="immozwerg-footer-contact-card"><Mail size={15} aria-hidden="true" /><span><span className="immozwerg-footer-contact-label">E-Mail</span>hallo@haj-immokg.com</span></a>
              <p className="flex items-center gap-2 px-1"><MapPin size={14} aria-hidden="true" /> Güglingen · Heilbronn & Umgebung</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {socialLinks.map(({ label, href, icon: Icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`${label} öffnen`} className="immozwerg-footer-social-link">
                  <Icon size={15} aria-hidden="true" /><span>{label}</span>
                </a>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/55 sm:flex-row">
          <span>© 2026 Immozwerg · HAJ Immo e.K. – Andrea Heinz. Alle Rechte vorbehalten.</span>
          <div className="flex gap-2">
            <Link href="/impressum" className="immozwerg-footer-link text-xs">Impressum</Link>
            <Link href="/datenschutz" className="immozwerg-footer-link text-xs">Datenschutz</Link>
          </div>
        </div>
        <div className="mt-6 border-t border-white/10 pt-2">
          <AccessibilityControls />
        </div>
      </div>
    </footer>
  );
}
